import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { updateNebudashProfile } from '../../ApiController/ApiService';
import { 
    FiHome, 
    FiBriefcase, 
    FiUser, 
    FiPhone, 
    FiMail, 
    FiMapPin, 
    FiArrowRight,
    FiCheckCircle
} from 'react-icons/fi';

const BUSINESS_TYPES = [
    'Retailer',
    'Warehouse',
    'Logistics Company',
    'Manufacturer',
    'Distributor',
    'Broker / 3PL',
    'Other'
];

const CompleteProfileModal = ({ isOpen, onClose, existingProfile, onProfileCompleted }) => {
    const [formData, setFormData] = useState({
        businessType: 'Retailer',
        companyName: '',
        storeName: '',
        contactPersonName: '',
        phoneNumber: '',
        primaryEmail: '',
        invoiceEmail: '',
        billingAddress: '',
        billingCity: '',
        billingState: '',
        billingZip: ''
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (existingProfile) {
            setFormData({
                businessType: existingProfile.businessType || 'Retailer',
                companyName: existingProfile.companyName || '',
                storeName: existingProfile.storeName || '',
                contactPersonName: existingProfile.contactPersonName || '',
                phoneNumber: existingProfile.phone || existingProfile.phoneNumber || '',
                primaryEmail: existingProfile.email || existingProfile.primaryEmail || '',
                invoiceEmail: existingProfile.invoiceEmail || '',
                billingAddress: existingProfile.billingAddress || '',
                billingCity: existingProfile.billingCity || '',
                billingState: existingProfile.billingState || '',
                billingZip: existingProfile.billingZip || ''
            });
        }
    }, [existingProfile]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (error) setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validations according to backend requirements
        if (!formData.businessType?.trim()) {
            setError('Please select a Business Type.');
            return;
        }

        if (!formData.companyName?.trim() && !formData.storeName?.trim()) {
            setError('Please provide at least a Company Name or Store Name.');
            return;
        }

        if (!formData.billingAddress?.trim()) {
            setError('Please enter your street billing address.');
            return;
        }

        if (!formData.billingCity?.trim()) {
            setError('Please enter your billing city.');
            return;
        }

        if (!formData.billingState?.trim()) {
            setError('Please enter your billing state.');
            return;
        }

        if (!formData.billingZip?.trim()) {
            setError('Please enter your billing ZIP code.');
            return;
        }

        const phoneVal = (formData.phoneNumber || formData.phone || '').trim();
        if (!phoneVal) {
            setError('Please enter your phone number.');
            return;
        }

        const emailVal = (formData.primaryEmail || formData.email || '').trim();
        if (!emailVal) {
            setError('Please enter your email address.');
            return;
        }

        try {
            setLoading(true);
            const payload = {
                businessType: formData.businessType.trim(),
                companyName: formData.companyName?.trim() || undefined,
                storeName: formData.storeName?.trim() || undefined,
                contactPersonName: formData.contactPersonName?.trim() || undefined,
                phone: phoneVal,
                phoneNumber: phoneVal,
                email: emailVal,
                primaryEmail: emailVal,
                invoiceEmail: formData.invoiceEmail?.trim() || undefined,
                billingAddress: formData.billingAddress.trim(),
                billingCity: formData.billingCity.trim(),
                billingState: formData.billingState.trim(),
                billingZip: formData.billingZip.trim()
            };

            const response = await updateNebudashProfile(payload);
            console.log('Profile update response:', response);

            Swal.fire({
                title: 'Profile Completed!',
                text: 'Your profile has been saved successfully.',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });

            if (onProfileCompleted) {
                onProfileCompleted(response?.data || payload);
            }
            if (onClose) {
                onClose();
            }
        } catch (err) {
            console.error('Failed to complete profile:', err);
            const resErrors = err?.response?.data?.errors;
            let errMsg = '';
            if (Array.isArray(resErrors) && resErrors.length > 0) {
                errMsg = resErrors.join(', ');
            } else {
                errMsg = err?.response?.data?.message || err?.message || 'Failed to save profile. Please check all fields.';
            }
            setError(errMsg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-5 overflow-y-auto">
            <div className="bg-[#0B1333] border border-blue-900/60 rounded-3xl shadow-2xl w-full max-w-3xl my-auto overflow-hidden animate-fadeIn flex flex-col text-white">
                
                {/* Header (Matching Image 1) */}
                <div 
                    className="p-6 sm:p-8 relative border-b border-white/10"
                    style={{
                        background: "radial-gradient(100% 100% at 50% 0%, #172554 0%, #0B1333 100%)"
                    }}
                >
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 mb-4 text-2xl shadow-inner">
                        🏪
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white mb-2">
                        Complete Your Profile
                    </h2>
                    <p className="text-sm text-slate-300">
                        Tell us about your business to unlock load posting and carrier matching.
                    </p>
                </div>

                {/* Body Form */}
                <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-6 max-h-[68vh] overflow-y-auto custom-scrollbar">
                    
                    {error && (
                        <div className="p-3.5 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
                            {error}
                        </div>
                    )}

                    {/* Section 1: Business Identity */}
                    <div className="bg-white rounded-2xl p-5 sm:p-6 text-gray-900 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-2 mb-4">
                            <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">
                                1
                            </span>
                            <h3 className="font-bold text-gray-900 text-base">Business Identity</h3>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                    Business Type <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base">
                                        <FiBriefcase />
                                    </span>
                                    <select
                                        name="businessType"
                                        value={formData.businessType}
                                        onChange={handleChange}
                                        className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    >
                                        {BUSINESS_TYPES.map(type => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                        Company Name
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base">
                                            <FiHome />
                                        </span>
                                        <input
                                            type="text"
                                            name="companyName"
                                            value={formData.companyName}
                                            onChange={handleChange}
                                            placeholder="E.g., Global Logistics LLC"
                                            className="w-full pl-10 pr-3.5 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                        Store Name
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base">
                                            <FiHome />
                                        </span>
                                        <input
                                            type="text"
                                            name="storeName"
                                            value={formData.storeName}
                                            onChange={handleChange}
                                            placeholder="E.g., Downtown Branch"
                                            className="w-full pl-10 pr-3.5 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section 2: Contact Details */}
                    <div className="bg-white rounded-2xl p-5 sm:p-6 text-gray-900 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-2 mb-4">
                            <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">
                                2
                            </span>
                            <h3 className="font-bold text-gray-900 text-base">Contact Details</h3>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                    Contact Person Name <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base">
                                        <FiUser />
                                    </span>
                                    <input
                                        type="text"
                                        name="contactPersonName"
                                        value={formData.contactPersonName}
                                        onChange={handleChange}
                                        placeholder="John Doe"
                                        className="w-full pl-10 pr-3.5 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                    Phone Number <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base">
                                        <FiPhone />
                                    </span>
                                    <input
                                        type="text"
                                        name="phoneNumber"
                                        value={formData.phoneNumber}
                                        onChange={handleChange}
                                        placeholder="+1 (555) 000-0000"
                                        className="w-full pl-10 pr-3.5 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                    Primary Email <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base">
                                        <FiMail />
                                    </span>
                                    <input
                                        type="email"
                                        name="primaryEmail"
                                        value={formData.primaryEmail}
                                        onChange={handleChange}
                                        placeholder="contact@company.com"
                                        className="w-full pl-10 pr-3.5 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                    Invoice Email <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base">
                                        <FiMail />
                                    </span>
                                    <input
                                        type="email"
                                        name="invoiceEmail"
                                        value={formData.invoiceEmail}
                                        onChange={handleChange}
                                        placeholder="billing@company.com"
                                        className="w-full pl-10 pr-3.5 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section 3: Billing Address */}
                    <div className="bg-white rounded-2xl p-5 sm:p-6 text-gray-900 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-2 mb-4">
                            <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">
                                3
                            </span>
                            <h3 className="font-bold text-gray-900 text-base">Billing Address</h3>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                    Street Address <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3.5 top-3 text-gray-400 text-base">
                                        <FiMapPin />
                                    </span>
                                    <textarea
                                        rows={2}
                                        name="billingAddress"
                                        value={formData.billingAddress}
                                        onChange={handleChange}
                                        placeholder="Enter complete street billing address..."
                                        className="w-full pl-10 pr-3.5 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                        City <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="billingCity"
                                        value={formData.billingCity}
                                        onChange={handleChange}
                                        placeholder="e.g. Dallas"
                                        className="w-full px-3.5 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                        State <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="billingState"
                                        value={formData.billingState}
                                        onChange={handleChange}
                                        placeholder="e.g. TX"
                                        className="w-full px-3.5 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                        ZIP Code <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="billingZip"
                                        value={formData.billingZip}
                                        onChange={handleChange}
                                        placeholder="e.g. 75001"
                                        className="w-full px-3.5 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bottom Action Bar */}
                    <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-2 text-xs text-emerald-400">
                            <FiCheckCircle className="text-sm shrink-0" />
                            <span>All fields marked with * are required</span>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full sm:w-auto px-7 py-3 rounded-xl bg-[#0F172A] hover:bg-[#1E293B] border border-white/10 text-white font-semibold text-sm transition-all flex items-center justify-center gap-2 shadow-lg disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                        >
                            {loading ? (
                                <>
                                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                    <span>Saving...</span>
                                </>
                            ) : (
                                <>
                                    <span>Complete Setup</span>
                                    <FiArrowRight className="text-base" />
                                </>
                            )}
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
};

export default CompleteProfileModal;
